import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { Button } from '../ui/button';
import { Gift, CreditCard, X } from 'lucide-react';

const DISMISS_KEY = 'free-access-banner-dismissed';

export default function FreeAccessBanner() {
  const navigate = useNavigate();
  const { subscriptionStatus } = useSubscription();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');

  const isFreeTrial = subscriptionStatus?.is_trial && !subscriptionStatus?.trial_expired;

  const { daysLeft, untilDate } = useMemo(() => {
    const raw = subscriptionStatus?.trial_end_date;
    const end = raw ? new Date(raw) : new Date('2027-02-01T00:00:00Z');
    const days = Math.max(0, Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24)));
    const label = end.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    return { daysLeft: days, untilDate: label };
  }, [subscriptionStatus?.trial_end_date]);

  // On the subscription page itself the free-period card already tells the story
  const onSubscriptionPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/landlord/subscription');

  if (!isFreeTrial || dismissed || onSubscriptionPage) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div
      className="mb-6 rounded-xl border border-[#6B8E6B]/40 bg-gradient-to-r from-[#6B8E6B]/12 via-[#6B8E6B]/6 to-transparent px-4 py-3 sm:px-5"
      data-testid="free-access-banner"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#6B8E6B] text-white">
            <Gift className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">
              Free until {untilDate}
              <span className="ml-2 inline-flex items-center rounded-full bg-[#6B8E6B] px-2 py-0.5 text-[11px] font-medium text-white" data-testid="free-access-days">
                {daysLeft} {daysLeft === 1 ? 'day' : 'days'} left
              </span>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Every feature is unlocked — no card required. Lock in your rate early whenever you're ready.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0 sm:pl-3">
          <Button
            size="sm"
            onClick={() => navigate('/landlord/subscription')}
            className="bg-[#C85A42] hover:bg-[#B04A35] text-white h-8"
            data-testid="free-access-subscribe-btn"
          >
            <CreditCard className="h-3.5 w-3.5 mr-1.5" />
            Subscribe now
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDismiss}
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            data-testid="free-access-dismiss-btn"
            title="Dismiss"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
